import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Sparkles, Loader2, Trash2 } from 'lucide-react';
import { api, getCurrentMonth } from '../../lib/api';

export const AIChat = ({ role = 'patron', page = 'dashboard' }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const accentColor = role === 'patron' ? 'rgb(var(--lm-gold))' : 'rgb(var(--lm-green))';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);

    try {
      const res = await api.post('/ai-advisor/chat', {
        message: msg,
        session_id: sessionId,
        page,
        role,
        mois: getCurrentMonth(),
      });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: 'Erreur de connexion. Réessayez.' }]);
    }
    setLoading(false);
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestions = role === 'patron'
    ? ['Comment améliorer ma marge ?', 'Quels fournisseurs renégocier ?', 'Mon ratio matière est-il bon ?']
    : ['Comment réduire les pertes ?', 'Budget restant suffisant ?', 'Quels achats prioriser ?'];

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="ai-chat-toggle"
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95"
        style={{
          background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`,
          boxShadow: `0 4px 24px ${accentColor}40`,
        }}
      >
        {open ? (
          <X size={22} className="text-[rgb(var(--lm-bg))]" />
        ) : (
          <MessageCircle size={22} className="text-[rgb(var(--lm-bg))]" />
        )}
      </button>

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-5 z-50 w-[360px] max-w-[calc(100vw-40px)] rounded-2xl overflow-hidden shadow-2xl flex flex-col animate-slide-up"
          style={{
            background: 'rgb(var(--lm-card))',
            border: `1px solid ${accentColor}30`,
            height: 'min(520px, calc(100vh - 140px))',
            boxShadow: `0 8px 40px rgba(0,0,0,0.4), 0 0 60px ${accentColor}10`,
          }}
          data-testid="ai-chat-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between shrink-0" style={{ borderBottom: `1px solid ${accentColor}20` }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${accentColor}20` }}>
                <Sparkles size={16} style={{ color: accentColor }} />
              </div>
              <div>
                <p className="text-sm font-medium text-[rgb(var(--lm-text))]">Max — Conseiller IA</p>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Expert restauration</p>
              </div>
            </div>
            {messages.length > 0 && (
              <button onClick={clearChat} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-bg))] transition-colors" data-testid="ai-chat-clear">
                <Trash2 size={14} className="text-[rgb(var(--lm-text-muted))]" />
              </button>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin" data-testid="ai-chat-messages">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full gap-4 py-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `${accentColor}15` }}>
                  <Sparkles size={24} style={{ color: accentColor }} />
                </div>
                <p className="text-xs text-[rgb(var(--lm-text-muted))] text-center max-w-[240px]">
                  Posez-moi n'importe quelle question sur votre restaurant
                </p>
                <div className="flex flex-col gap-2 w-full px-2">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => { setInput(s); inputRef.current?.focus(); }}
                      className="text-left text-xs px-3 py-2 rounded-lg transition-colors hover:bg-[rgb(var(--lm-bg))]"
                      style={{ border: `1px solid ${accentColor}15`, color: 'rgb(var(--lm-text-secondary))' }}
                      data-testid={`ai-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'rounded-br-md'
                      : 'rounded-bl-md'
                  }`}
                  style={
                    m.role === 'user'
                      ? { background: accentColor, color: 'rgb(var(--lm-bg))' }
                      : { background: 'rgb(var(--lm-bg))', color: 'rgb(var(--lm-text-secondary))' }
                  }
                >
                  <p className="whitespace-pre-wrap break-words">{m.text}</p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-md px-4 py-3" style={{ background: 'rgb(var(--lm-bg))' }}>
                  <div className="flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" style={{ color: accentColor }} />
                    <span className="text-xs text-[rgb(var(--lm-text-muted))]">Max réfléchit...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 shrink-0" style={{ borderTop: `1px solid ${accentColor}15` }}>
            <div className="flex items-center gap-2 rounded-xl px-3 py-1.5" style={{ background: 'rgb(var(--lm-bg))', border: `1px solid ${accentColor}20` }}>
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Posez votre question..."
                className="flex-1 bg-transparent text-sm text-[rgb(var(--lm-text))] placeholder:text-[rgb(var(--lm-text-muted))] outline-none py-1.5"
                disabled={loading}
                data-testid="ai-chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="p-1.5 rounded-lg transition-all disabled:opacity-30 hover:scale-110 active:scale-95"
                data-testid="ai-chat-send"
              >
                <Send size={16} style={{ color: accentColor }} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
